import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { NgChartsModule } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { IDropdownSettings, NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';


interface StatisticsByDayDTO {
  dataAffido: string;
  statoUno: number;
  statoDue: number;
  statoTre: number;
  statoQuattro: number;
  statoCinque: number;
  consegnatiInOrario: number;
}

interface Client {
  clientCode: string;
  clientName?: string;
}

interface Brand {
  brandCode: string;
  brandName?: string;
}

interface ClientsBrands {
  clientCode: string;
  clientName?: string;
  brands: Brand[];
}

@Component({
  selector: 'app-statistics-returns-by-day',
  standalone: true,
  templateUrl: './statistics-returns-by-day.component.html',
  styleUrls: ['./statistics-returns-by-day.component.css'],
  imports: [FormsModule, CommonModule, TranslateModule, NgChartsModule, NgMultiSelectDropDownModule]
})
export class StatisticsByDayReturnsComponent implements OnInit {

  stats?: StatisticsByDayDTO[];
  loading = false;
  error = '';

  dateBefore26: boolean = false;

  // Filtri selezionati
  dateStart: string = new Date('2025-06-02').toISOString().split('T')[0];
  dateEnd: string = new Date().toISOString().split('T')[0];
  type: string = '';

  selectedToggle: 'affido' | 'creazione' = 'affido';

  chartAffidatiData: ChartData<'bar' | 'line'> | undefined;
  chartConsegnatiData: ChartData<'bar' | 'line'>  | undefined;
  chartOptions: ChartOptions | undefined;   public ChartDataLabels = ChartDataLabels;   
  sortState = { active: '', direction: '' };

  filteredBrands: Brand[] = [];
  selectedBrands: Brand[] = [];
  filteredClients: Client[] = [];
  selectedClients: Client[] = [];
  clientsBrands: ClientsBrands[] = [];
  
  dropdownSettingsBrand: IDropdownSettings = {};
  dropdownSettingsClient: IDropdownSettings = {};

   // Sistema di paginazione (copiato da dealers-list)
   pageIndex = 0;
   pageSize = 10; // Valore di default, verrà sovrascritto nel constructor
   totalItems = 0;
   private readonly PAGE_SIZE_KEY = 'statistics-by-day-page-size';
  constructor(private http: HttpClient) {
    this.pageSize = this.getStoredPageSize(); // Inizializza il pageSize nel constructor
  }

  private getStoredPageSize(): number {
    const stored = localStorage.getItem(this.PAGE_SIZE_KEY);
    return stored ? parseInt(stored, 10) : 10; // Default a 10 se non trovato
  }

  private savePageSize(size: number): void {
    localStorage.setItem(this.PAGE_SIZE_KEY, size.toString());
  }  
  
  ngOnInit(): void {
    this.sortState = { active: 'dataAffido', direction: 'desc' };
    this.confrontaDate();
    
    const stored = localStorage.getItem('userFunctions');
    if(stored) {
      const parsed = JSON.parse(stored);
      this.clientsBrands = parsed.clients; 

      this.filteredClients = this.clientsBrands.map(cb => ({
        clientCode: cb.clientCode,
        clientName: cb.clientName,
      }))
      .sort((a, b) => {
        if (!a.clientName && !b.clientName) return 0;       
        if (!a.clientName) return 1;                        
        if (!b.clientName) return -1;                       
        return a.clientName.localeCompare(b.clientName);   
      });

      const allBrands: Brand[] = this.clientsBrands.flatMap(cb =>
        cb.brands.map(b => ({
          brandCode: b.brandCode,
          brandName: b.brandName,
        }))
      ).sort((a, b) => {
        if (!a.brandName && !b.brandName) return 0;       
        if (!a.brandName) return 1;                        
        if (!b.brandName) return -1;                       
        return a.brandName.localeCompare(b.brandName);   
      });

      this.filteredBrands = Array.from(
        new Map(allBrands.map(b => [b.brandCode, b])).values()
      );

      this.dropdownSettingsBrand = {
        singleSelection: false,
        idField: 'brandCode',
        textField: 'brandName',
        selectAllText: 'Seleziona tutti',
        unSelectAllText: 'Deseleziona tutti',
        itemsShowLimit: 3,
        allowSearchFilter: true,
        searchPlaceholderText: 'Cerca'
      };
      this.dropdownSettingsClient = {
        singleSelection: false,
        idField: 'clientCode',
        textField: 'clientName',
        selectAllText: 'Seleziona tutti',
        unSelectAllText: 'Deseleziona tutti',
        itemsShowLimit: 3,
        allowSearchFilter: true,
        searchPlaceholderText: 'Cerca'
      };
  
      this.fetchStatisticsByAffido();
    }
  }

  onClientsChange(): void {
    this.getIntersectionClientsBrands();
  }

  getIntersectionClientsBrands() : void {
    if ((!this.selectedClients || this.selectedClients.length === 0)) {
      // Nessun client selezionato → mostro tutti i brands
      this.filteredBrands = Array.from(
        new Map(
          this.clientsBrands
            .flatMap(cb => cb.brands)
            .map(b => [b.brandCode, b])
        ).values()
      );
      //this.selectedBrands = []; // reset
      this.selectedBrands = this.selectedBrands.filter(b =>
        this.filteredBrands.some(fb => fb.brandCode === b.brandCode)
      );
      return;
    }

    // prendo i codici dei client selezionati
    const selectedCodes = this.selectedClients.map(c => c.clientCode);

    // ottengo i brands per ogni client selezionato
    const brandLists = selectedCodes.map(code => {
      const client = this.clientsBrands.find(cb => cb.clientCode === code);
      return client ? client.brands : [];
    });


    // calcolo l’intersezione tra tutte le liste
    const intersection = brandLists.reduce((acc, curr) => {
      return acc.filter(b =>
        curr.some(x => x.brandCode === b.brandCode)
      );
    });


    // aggiorno i brands disponibili
    this.filteredBrands = intersection;
    this.selectedBrands = []; // reset per evitare selezioni incoerenti
  }


  selectAllClients() {
    this.selectedClients = [...this.filteredClients];

    this.getIntersectionClientsBrands();
  }

  deselectAllClients() {
    this.selectedClients = [];

    this.getIntersectionClientsBrands();
  }

  selectAllBrands() {
    this.selectedBrands = [...this.filteredBrands];
  }

  deselectAllBrands() {
    this.selectedBrands = [];
  }

  confrontaDate(): void {
    this.dateBefore26 = false;
    
    const start = new Date(this.dateStart);
    const data = new Date(this.dateEnd);

    start.setHours(0, 0, 0, 0);
    data.setHours(0, 0, 0, 0);

    if (data <= start) {
      this.dateBefore26 = true;
    }
  }

  getClientsBrandsSelected() : ClientsBrands[] {
    let clientsBrandsSelected: ClientsBrands[];
    if(this.filteredClients.length == 1) {
      this.selectedClients = [...this.filteredClients];
    }
    if (this.selectedClients.length > 0) {
      clientsBrandsSelected = this.selectedClients.map(sc => {
        // trovo il client originale da clientsBrands
        const originalClient = this.clientsBrands.find(cb => cb.clientCode === sc.clientCode);

        var filteredBrandsFromClient: Brand[] = [];
        if(this.selectedBrands.length > 0) {
          // brands effettivi = intersezione tra quelli del client e quelli selezionati
          filteredBrandsFromClient = (originalClient?.brands || []).filter(b =>
            this.selectedBrands.some(sb => sb.brandCode === b.brandCode)
          );
        }
        else {
          filteredBrandsFromClient = this.filteredBrands;
        }

        return {
          clientCode: sc.clientCode,
          clientName: sc.clientName,
          brands: filteredBrandsFromClient
        } as ClientsBrands;
      });
    } else if (this.selectedBrands && this.selectedBrands.length > 0) {
      // nessun client selezionato, ma brand sì
      clientsBrandsSelected = this.clientsBrands
        .map(cb => {
          const matchingBrands = cb.brands.filter(b =>
            this.selectedBrands.some(sb => sb.brandCode === b.brandCode)
          );
          if (matchingBrands.length > 0) {
            return {
              clientCode: cb.clientCode,
              clientName: cb.clientName,
              brands: matchingBrands
            } as ClientsBrands;
          }
          return null;
        })
        .filter(cb => cb !== null) as ClientsBrands[];

    } else {
      // né client né brand selezionati → ritorno tutto
      clientsBrandsSelected = this.clientsBrands;
    }


    return clientsBrandsSelected;
  }

  fetchStatisticsByAffido(): void {
    this.loading = true;
    this.error = '';
    this.stats = undefined;

    let clientsBrandsSelected: ClientsBrands[] = this.getClientsBrandsSelected();
    
    const body = {
          clients: clientsBrandsSelected || undefined,
          dateStart: this.dateStart || undefined,
          dateEnd: this.dateEnd || undefined,
          type: this.type || undefined
        };

    this.http.post<StatisticsByDayDTO[]>(`${environment.apiUrl}/statistics/statisticsByDayReturns`, body)
      .subscribe({
        next: (data) => {
          this.stats = data;
          this.applySortManual();
          this.updatePagination();
          this.loading = false;
        },
        error: (err) => {
          this.error = 'Errore nel caricamento delle statistiche.';
          this.loading = false;
        }
      });
    this.confrontaDate();
  }

    fetchStatisticsByCreazione(): void {
    this.loading = true;
    this.error = '';
    this.stats = undefined;
    
    let clientsBrandsSelected: ClientsBrands[] = this.getClientsBrandsSelected();
    const body = {
      clients: clientsBrandsSelected || undefined,
      dateStart: this.dateStart || undefined,
      dateEnd: this.dateEnd || undefined,
      type: this.type || undefined
    };
    this.http.post<StatisticsByDayDTO[]>(`${environment.apiUrl}/statistics/statisticsByDayCreationReturns`, body)
      .subscribe({
        next: (data) => {
          this.stats = data;
          this.applySortManual();
          this.updatePagination();
          this.loading = false;
        },
        error: (err) => {
          this.error = 'Errore nel caricamento delle statistiche.';
          this.loading = false;
        }
      });
    this.confrontaDate();
  }

  resetFilters(): void {
    this.selectedBrands = [];
    this.dateStart= new Date('2025-06-02').toISOString().split('T')[0];
    this.dateEnd = new Date().toISOString().split('T')[0];
    this.type = '';
    this.fetchStatisticsByAffido();
  }

  exportStatisticsToExcel(): void {

    if (this.stats != null && this.stats.length > 0) {
      let exportData;

if (this.selectedToggle === 'creazione') {
    exportData = this.stats.map(s => ({
      'Data Creazione': new Date(s.dataAffido).toLocaleDateString('it-IT'), // ← usa dataAffido come data creazione
      'Stato 1': s.statoUno,
      'Stato 2': s.statoDue,
      'Stato 3': s.statoTre,
      'Stato 4': s.statoQuattro,
      'Stato 5': s.statoCinque,
      'Consegnati in orario': s.consegnatiInOrario
    }));
  } else {
    exportData = this.stats.map(s => ({
      'Data Affido': new Date(s.dataAffido).toLocaleDateString('it-IT'),
      'Stato 1': s.statoUno,
      'Stato 2': s.statoDue,
      '% Stato 2': this.getPercentage(s.statoDue, s.statoUno),
      'Stato 3': s.statoTre,
      '% Stato 3': this.getPercentage(s.statoTre, s.statoUno),
      'Stato 4': s.statoQuattro,
      '% Stato 4': this.getPercentage(s.statoQuattro, s.statoUno),
      'Stato 5': s.statoCinque,
      '% Stato 5': this.getPercentage(s.statoCinque, s.statoUno),
      'Consegnati in orario': s.consegnatiInOrario,
      '% in orario': this.getPercentage(s.consegnatiInOrario, s.statoUno)
    }));
  }

      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  
      const blobData = new Blob([excelBuffer], { type: 'application/octet-stream' });
      const now = new Date();
      const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;

      FileSaver.saveAs(blobData, `statistiche_resi_${timestamp}.xlsx`);
    }
  }

  getPercentage(part: number, total: number): string {
  if (!total) return '-';
  return ((part / total) * 100).toFixed(2) + '%';
}


  setSort(column: string) {
    if (this.sortState.active === column) {
      this.sortState.direction = this.sortState.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.active = column;
      this.sortState.direction = 'asc';
    }
    this.applySortManual();
    this.pageIndex = 0; // Reset alla prima pagina quando si cambia ordinamento
    this.updatePagination(); // Aggiorna la paginazione
  }  applySortManual(): void {
    if (this.stats && this.sortState.active && this.sortState.direction) {
      const isAsc = this.sortState.direction === 'asc';
      this.stats = [...this.stats].sort((a, b) => {
      const isAsc = this.sortState.direction === 'asc';
      switch (this.sortState.active) {
        case 'dataAffido': return this.compare(a.dataAffido, b.dataAffido, isAsc);
        case 'statoUno': return this.compare(a.statoUno, b.statoUno, isAsc);
        case 'statoDue': return this.compare(a.statoDue, b.statoDue, isAsc);
        case 'statoTre': return this.compare(a.statoTre, b.statoTre, isAsc);
        case 'statoQuattro': return this.compare(a.statoQuattro, b.statoQuattro, isAsc);
        case 'statoCinque': return this.compare(a.statoCinque, b.statoCinque, isAsc);
        case 'consegnatiInOrario': return this.compare(a.consegnatiInOrario, b.consegnatiInOrario, isAsc);
        default: return 0;
      }
    });
      this.updatePagination(); // Aggiorna la paginazione dopo il sort
    }
  }
  compare(a: string | number, b: string | number, isAsc: boolean): number {
    if (typeof a === 'string' && typeof b === 'string') {
      return a.localeCompare(b) * (isAsc ? 1 : -1);
    }
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }  updatePagination(): void {
    this.totalItems = this.stats?.length || 0;
    // Assicura che pageIndex sia valido dopo l'aggiornamento
    const totalPages = this.getNumberOfPages();
    if (this.pageIndex >= totalPages && totalPages > 0) {
      this.pageIndex = totalPages - 1;
    }
  }
  getNumberOfPages(): number {
    return Math.ceil(this.totalItems / Number(this.pageSize)) || 1;
  }  setPageSize(size: number): void {
    this.pageSize = Number(size);
    this.savePageSize(this.pageSize);
    this.pageIndex = 0;
    this.updatePagination();
  }getPaginatedStats(): StatisticsByDayDTO[] {
    if (!this.stats || this.stats.length === 0) return [];
    
    const pageSize = Number(this.pageSize); // Assicura che sia un numero
    const startIndex = this.pageIndex * pageSize;
    const endIndex = Math.min(startIndex + pageSize, this.stats.length);
    
    const result = this.stats.slice(startIndex, endIndex);
    
    // Verifica che il risultato non superi mai pageSize
    if (result.length > pageSize) {
      return result.slice(0, pageSize); // Forza il limite
    }
    
    return result;
  }firstPage(): void {
    this.pageIndex = 0;
  }

  previousPage(): void {
    if (this.pageIndex > 0) {
      this.pageIndex--;
    }
  }  nextPage(): void {
    if (this.pageIndex < this.getNumberOfPages() - 1) {
      this.pageIndex++;
    }
  }
  lastPage(): void {
    const totalPages = this.getNumberOfPages();
    this.pageIndex = Math.max(totalPages - 1, 0);
  }

    // Funzioni per navigazione date
  decrementStartDate(): void {
    if (this.dateStart) {
      const date = new Date(this.dateStart);
      date.setDate(date.getDate() - 1);
      this.dateStart = date.toISOString().split('T')[0];
    }
  }

  incrementStartDate(): void {
    if (this.dateStart) {
      const date = new Date(this.dateStart);
      date.setDate(date.getDate() + 1);
      this.dateStart = date.toISOString().split('T')[0];
    }
  }

  decrementEndDate(): void {
    if (this.dateEnd) {
      const date = new Date(this.dateEnd);
      date.setDate(date.getDate() - 1);
      this.dateEnd = date.toISOString().split('T')[0];
    }
  }

  incrementEndDate(): void {
    if (this.dateEnd) {
      const date = new Date(this.dateEnd);
      date.setDate(date.getDate() + 1);
      this.dateEnd = date.toISOString().split('T')[0];
    }
  }

  getBadgeClass(current: number, base: number): string {
  if (!base || base === 0) return 'bg-secondary';
  const percent = (current / base) * 100;
  if (percent >= 80) return 'underline-green';
  if (percent >= 50) return 'underline-orange';
  return 'underline-red';
}


onToggle(selection: 'affido' | 'creazione') {
  this.selectedToggle = selection;
  
  if (selection === 'affido') {
    this.fetchStatisticsByAffido();
  } else if (selection === 'creazione') {
    this.fetchStatisticsByCreazione();
  }
}

onSubmit() {
  if (this.selectedToggle === 'affido') {
    this.fetchStatisticsByAffido();
  } else {
    this.fetchStatisticsByCreazione();
  }
}
}
